import React, { createContext, useEffect, useLayoutEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { IRootState, useAppDispatch, useAppSelector } from 'app/config/store';
import SidebarWrapper from 'app/shared/layout/sidebar/sidebar-wrapper';
import { useSelector } from 'react-redux';
import { SidebarItemPair } from 'app/shared/layout/sidebar/configuration/sidebar-configuration';
import { FullScreenLoader } from 'app/shared/loader/fullscreen-loader';
import CasePreviewHeader from 'app/edozvola/cases/header/case-preview-header';
import { fetchCaseInPreview } from 'app/shared/reducers/case-entity';
import { LOCAL_STORAGE_KEYS } from 'app/shared/util/local-storage-keys';
import { CasesGuard } from 'app/edozvola/cases/cases-guard';

export const CasePreviewTabContext = createContext({ sidebarItemPair: null, setSidebarItemPair: null });

const CaseFormPreviewWrapper = ({ children }) => {
  const path = useLocation();
  const { id } = useParams<{ id: string }>();
  const appDispatch = useAppDispatch();
  const currentCaseTabPath = path.pathname.split('/').pop();
  const [sidebarItemPair, setSidebarItemPair] = useState<SidebarItemPair>(null);
  const [fetchingCaseFinished, setFetchingCaseFinished] = useState<boolean>(false);

  const selectedCase = useSelector((state: IRootState) => state.caseEntity);

  useLayoutEffect(() => {
    if (selectedCase.idCase !== id) {
      appDispatch(fetchCaseInPreview(id));
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(LOCAL_STORAGE_KEYS.CASE_PREVIEW_RETURN, path.state?.from);
  }, []);

  useEffect(() => {
    if (selectedCase?.caseTabs && selectedCase?.caseTabs.length > 0) {
      setFetchingCaseFinished(false);
      setSidebarItemPair(findTabByPath(currentCaseTabPath));
      setTimeout(() => setFetchingCaseFinished(true));
    }
  }, [selectedCase]);

  const findFirstTab = () => {
    let firstCaseTab = null;

    selectedCase?.caseTabs.find(caseTab => {
      if (caseTab.sidebarItemPath === null) {
        caseTab.children.find(childTab => {
          if (childTab.sidebarItemPath !== null && firstCaseTab === null) {
            firstCaseTab = childTab;
            return true;
          }
          return false;
        });
      } else if (firstCaseTab === null) {
        firstCaseTab = caseTab;
        return true;
      }
      return false;
    });

    return firstCaseTab;
  };

  const findTabByPath = (path: string) => {
    let firstCaseTab = null;

    selectedCase?.caseTabs.find(caseTab => {
      if (caseTab.sidebarItemPath === null) {
        caseTab.children.find(childTab => {
          if (childTab.sidebarItemPath !== null && firstCaseTab === null && childTab.sidebarItemPath === path) {
            firstCaseTab = childTab;
            return true;
          }
          return false;
        });
      } else if (firstCaseTab === null && caseTab.sidebarItemPath === path) {
        firstCaseTab = caseTab;
        return true;
      }
      return false;
    });

    return firstCaseTab;
  };

  return (
    <>
      {!fetchingCaseFinished && <FullScreenLoader />}
      {fetchingCaseFinished &&
        (children ? (
          location.pathname.includes('/case-file-inspection') ? (
            <CasePreviewTabContext.Provider value={{ sidebarItemPair, setSidebarItemPair }}>
              <SidebarWrapper
                webId={selectedCase?.webId}
                navigationItems={selectedCase?.caseTabs?.map(item => ({ ...item, collapsed: true }))}
                relativePath={true}
                returnUrl={sessionStorage.getItem(LOCAL_STORAGE_KEYS.CASE_PREVIEW_RETURN)}
              >
                <CasePreviewHeader
                  headerTitle={
                    sessionStorage.getItem(LOCAL_STORAGE_KEYS.CASE_PREVIEW_RETURN) === 'case-file-inspection'
                      ? 'Uvid u spis predmeta'
                      : 'Pregled detalja predmeta'
                  }
                />
                {children}
              </SidebarWrapper>
            </CasePreviewTabContext.Provider>
          ) : (
            <CasesGuard>
              <CasePreviewTabContext.Provider value={{ sidebarItemPair, setSidebarItemPair }}>
                <SidebarWrapper
                  webId={selectedCase?.webId}
                  navigationItems={selectedCase?.caseTabs?.map(item => ({ ...item, collapsed: true }))}
                  relativePath={true}
                  returnUrl={sessionStorage.getItem(LOCAL_STORAGE_KEYS.CASE_PREVIEW_RETURN)}
                >
                  <CasePreviewHeader
                    headerTitle={
                      sessionStorage.getItem(LOCAL_STORAGE_KEYS.CASE_PREVIEW_RETURN) === 'case-file-inspection'
                        ? 'Uvid u spis predmeta'
                        : 'Pregled detalja predmeta'
                    }
                  />
                  {children}
                </SidebarWrapper>
              </CasePreviewTabContext.Provider>
            </CasesGuard>
          )
        ) : selectedCase?.caseTabs && selectedCase?.caseTabs.length > 0 ? (
          <Navigate to={findFirstTab().sidebarItemPath} state={{ from: sessionStorage.getItem(LOCAL_STORAGE_KEYS.CASE_PREVIEW_RETURN) }} />
        ) : (
          <Navigate to={'/my-cases'} />
        ))}
    </>
  );
};

export default CaseFormPreviewWrapper;
